// src/admin/components/MerchantDetailSheet.jsx
// Mirrors CustomerHistorySheet.jsx's pattern exactly: fetches on open,
// keyed by a single id, never batched across the merchants list. Only
// hits GET /admin/merchants/:id when a row is actually clicked.
//
// Deliberately does NOT show individual orders or any customer-level
// data -- only aggregate business_volume (revenue last 30d/1yr/lifetime)
// and support_contact frequency, per Prince's instruction not to see
// "orders and other personal details" from this panel.
//
// currency comes from the merchant's own businesses.currency column
// (set from IP geolocation at signup) via the API response — no
// hardcoding here anymore.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, TrendingUp, CreditCard, Instagram, Store, Tag, Receipt, Globe, RefreshCcw, CalendarPlus, Gift } from "lucide-react";
import adminApi from "@/admin/lib/adminApi";
import { formatMoney } from "@/lib/currency";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

const PLAN_LABELS = { free: "Free", creator: "Creator", starter: "Starter", growth: "Growth", pro: "Pro" };

// Built into every modern browser — no country-name library needed.
// Falls back to the bare code if the browser can't resolve it or the
// code isn't a real ISO region.
function countryLabel(code) {
  if (!code) return "—";
  const upper = code.toUpperCase();
  try {
    const name = new Intl.DisplayNames(["en"], { type: "region" }).of(upper);
    return name && name !== upper ? `${name} (${upper})` : upper;
  } catch {
    return upper;
  }
}

function StatCard({ label, value, tone = "bg-ink/5" }) {
  return (
    <div className={`rounded-2xl p-3 text-center ${tone}`}>
      <p className="text-xs uppercase tracking-[0.15em] text-ink/60">{label}</p>
      <p className="font-display text-xl font-black">{value}</p>
    </div>
  );
}

export default function MerchantDetailSheet({ open, onOpenChange, merchantId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [grantingDays, setGrantingDays] = useState(null); // 5 | 30 | null

  useEffect(() => {
    if (!open || !merchantId) return;
    setLoading(true);
    setError(null);
    adminApi
      .post("/admin/merchants/detail", { id: merchantId })
      .then((r) => setData(r.data))
      .catch(() => setError("Couldn't load this merchant."))
      .finally(() => setLoading(false));
  }, [open, merchantId]);

  // One-click manual rebuild of this merchant's Public Catalog Page cache.
  // Calls the same admin endpoint used everywhere else -- no rebuild logic
  // duplicated here. Safe to call even if their public catalog is off or
  // they're on Free; the endpoint just reports that back in the response.
  const rebuildCache = async () => {
    if (!merchantId || rebuilding) return;
    setRebuilding(true);
    try {
      const { data: res } = await adminApi.post("/admin/rebuild-catalog-cache", {
        business_id: merchantId,
      });
      if (res.public_catalog_enabled === false) {
        toast.warning("Cache rebuilt — but their Public Catalog Page is currently off, so nothing is being served from it.");
      } else {
        toast.success("Public Catalog Page cache rebuilt.");
      }
    } catch {
      toast.error("Couldn't rebuild the cache. Please try again.");
    } finally {
      setRebuilding(false);
    }
  };

  // Extends subscription_expires_at by 5 or 30 days. Blocked server-side
  // for Free plan (no expiry to extend) -- button is disabled below too.
  const grantExtraDays = async (days) => {
    if (!merchantId || grantingDays) return;
    setGrantingDays(days);
    try {
      const { data: res } = await adminApi.post("/admin/grant-extra-days", {
        business_id: merchantId,
        days,
      });
      setData((prev) => ({
        ...prev,
        billing: {
          ...prev.billing,
          subscription_expires_at: res.subscription_expires_at,
          extra_days_granted_total: res.extra_days_granted_total,
        },
      }));
      toast.success(`Granted ${days} extra days — now expires ${new Date(res.subscription_expires_at).toLocaleDateString()}.`);
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "free_plan_no_expiry") {
        toast.warning("Free plan has no expiry to extend — switch them to a paid plan first.");
      } else {
        toast.error("Couldn't grant extra days. Please try again.");
      }
    } finally {
      setGrantingDays(null);
    }
  };

  const b = data?.billing;
  const u = data?.usage;
  const rev = data?.business_volume;
  const ref = data?.referrals;
  const contact = data?.support_contact;
  const activity = data?.activity;
  const coupons = data?.coupons_used || [];
  const billingHistory = data?.billing_history;
  const automation = data?.automation;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto bg-cream sm:max-w-xl"
        data-testid="merchant-detail-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl tracking-tight">
            {data?.name || "Merchant"}
          </SheetTitle>
          <SheetDescription>
            Billing, usage, and activity summary — no order or customer detail.
          </SheetDescription>
        </SheetHeader>

        {loading || !data ? (
          error ? (
            <p className="mt-10 text-center text-sm text-ink/50">{error}</p>
          ) : (
            <div className="mt-10 flex items-center justify-center">
              <Loader2 className="animate-spin text-hotpink" />
            </div>
          )
        ) : (
          <div className="mt-6 space-y-6">
            {/* Identity */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-sunset-gradient text-white">
                  <Instagram size={16} />
                </span>
                <div>
                  <p className="font-display text-lg font-black tracking-tight">{data.name}</p>
                  <p className="text-xs text-ink/50">
                    {data.ig_username ? (
                      <a
                        href={`https://instagram.com/${data.ig_username}`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-dotted underline-offset-2 hover:text-hotpink"
                      >
                        @{data.ig_username}
                      </a>
                    ) : (
                      "—"
                    )}{" "}
                    · {countryLabel(data.country)}
                  </p>
                </div>
              </div>
              <p className="mt-3 text-xs text-ink/50">
                Merchant since {data.created_at ? new Date(data.created_at).toLocaleDateString() : "—"}
              </p>
              {data.ig_token_expires_at && (() => {
                const daysLeft = Math.ceil(
                  (new Date(data.ig_token_expires_at) - Date.now()) / (1000 * 60 * 60 * 24)
                );
                const warn = daysLeft <= 15;
                return (
                  <p className={`mt-1 text-xs ${warn ? "font-semibold text-hotpink" : "text-ink/40"}`}>
                    IG connection {daysLeft < 0 ? "expired" : `expires in ${daysLeft}d`}
                    {" "}({new Date(data.ig_token_expires_at).toLocaleDateString()})
                  </p>
                );
              })()}
            </div>

            {/* Billing */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="merchant-billing">
              <div className="flex items-center gap-2">
                <CreditCard size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Billing</p>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink/50">Plan</p>
                  <p className="font-semibold">{PLAN_LABELS[b?.plan] || b?.plan || "—"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink/50">Billing cycle</p>
                  <p className="font-semibold capitalize">{b?.billing_cycle || "—"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink/50">Status</p>
                  <p className="font-semibold capitalize">{b?.subscription_status || "—"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink/50">Expires</p>
                  <p className="font-semibold">
                    {b?.subscription_expires_at
                      ? new Date(b.subscription_expires_at).toLocaleDateString()
                      : "—"}
                  </p>
                </div>
                {b?.pending_plan && (
                  <div className="col-span-2 rounded-xl bg-lilac/40 px-3 py-2 text-xs">
                    Switches to{" "}
                    <strong>
                      {PLAN_LABELS[b.pending_plan] || b.pending_plan}
                      {b.pending_plan_billing_cycle
                        ? ` ${b.pending_plan_billing_cycle.charAt(0).toUpperCase()}${b.pending_plan_billing_cycle.slice(1)}`
                        : ""}
                    </strong>
                    {/* The switch fires off the CURRENT plan's expiry (subscription_expires_at),
                        matching Deactivate Expired Plans Cron V8 and the renewal reminder email --
                        pending_expires_at is a different field: what the NEW plan's expiry becomes
                        after the switch, not when the switch happens. */}
                    {b.subscription_expires_at
                      ? ` on ${new Date(b.subscription_expires_at).toLocaleDateString()}`
                      : ""}
                    {b.pending_expires_at && (
                      <span className="block text-ink/50">
                        New plan then runs until {new Date(b.pending_expires_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink/50">AI calls this month</p>
                  <p className="font-semibold">
                    {u?.ai_calls_this_month ?? 0} / {u?.ai_call_limit ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink/50">Team seats</p>
                  <p className="font-semibold">{u?.team_members_limit ?? 0}</p>
                </div>
              </div>
            </div>

            {/* Account controls — grant extra days */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="merchant-account-controls">
              <div className="flex items-center gap-2">
                <CalendarPlus size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Account controls</p>
              </div>

              <div className="mt-5 border-t border-ink/10 pt-4">
                <div className="flex items-center gap-2">
                  <CalendarPlus size={14} className="text-hotpink" />
                  <p className="text-xs font-bold uppercase tracking-wide text-ink/60">Grant extra usage days</p>
                </div>
                <p className="mt-1 text-xs text-ink/50">
                  Pushes their current plan's expiry out — doesn't apply to Free.
                </p>
                <div className="mt-2 flex gap-2">
                  {[5, 30].map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => grantExtraDays(days)}
                      disabled={grantingDays !== null || b?.plan === "free"}
                      className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3.5 py-1.5 text-xs font-semibold hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {grantingDays === days ? <Loader2 size={12} className="animate-spin" /> : `+${days} days`}
                    </button>
                  ))}
                </div>
                {b?.extra_days_granted_total > 0 && (
                  <p className="mt-2 text-xs text-ink/50">{b.extra_days_granted_total} extra days granted lifetime.</p>
                )}
              </div>
            </div>

            {/* Business volume */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="merchant-revenue">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Business volume</p>
              </div>
              <p className="mt-1 text-xs text-ink/50">Paid orders total, not individual orders.</p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <StatCard label="Last 30 days" value={formatMoney(rev?.revenue_last_30_days, data.currency)} tone="bg-mint/60" />
                <StatCard label="Last year" value={formatMoney(rev?.revenue_last_year, data.currency)} tone="bg-peach/60" />
                <StatCard label="Lifetime" value={formatMoney(rev?.revenue_lifetime, data.currency)} />
              </div>
              <p className="mt-3 text-center text-xs text-ink/50">
                {rev?.orders_paid_total ?? 0} paid orders lifetime
              </p>
            </div>

            {/* Support contact frequency */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="merchant-contact-frequency">
              <div className="flex items-center gap-2">
                <MessageCircle size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Reached out to you</p>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <StatCard label="Last 30 days" value={contact?.contacts_last_30_days ?? 0} tone="bg-mint/60" />
                <StatCard label="Last year" value={contact?.contacts_last_year ?? 0} tone="bg-peach/60" />
                <StatCard label="All time" value={contact?.total_contacts ?? 0} />
              </div>
              {contact?.last_contacted_at && (
                <p className="mt-3 text-center text-xs text-ink/50">
                  Last contacted {new Date(contact.last_contacted_at).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Activity */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="merchant-activity">
              <div className="flex items-center gap-2">
                <Store size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Activity</p>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <StatCard
                  label="Team seats filled"
                  value={`${activity?.team_members_active ?? 0} / ${u?.team_members_limit ?? 0}`}
                  tone="bg-mint/60"
                />
                <StatCard label="Products listed" value={activity?.catalog_items_count ?? 0} tone="bg-peach/60" />
                <StatCard label="Comment automations" value={automation?.active_comment_rules ?? 0} />
                <StatCard label="Story automations" value={automation?.active_story_rules ?? 0} />
              </div>
            </div>

            {/* Public Catalog Page — manual cache rebuild */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="merchant-public-catalog">
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Public Catalog Page</p>
              </div>
              <p className="mt-1 text-xs text-ink/50">
                Force-refresh their storefront cache — useful when a product, image, price, or
                coupon change isn't showing up yet.
              </p>
              <button
                type="button"
                onClick={rebuildCache}
                disabled={rebuilding}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-xs font-bold text-cream transition hover:bg-ink/85 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {rebuilding ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <RefreshCcw size={13} />
                )}
                {rebuilding ? "Rebuilding…" : "Rebuild cache now"}
              </button>
            </div>

            {/* Coupons used */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="merchant-coupons">
              <div className="flex items-center gap-2">
                <Tag size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Coupons used</p>
              </div>
              {coupons.length === 0 ? (
                <p className="mt-3 text-sm text-ink/50">No coupon used.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {coupons.map((c, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-xl bg-cream px-3 py-2 text-sm"
                    >
                      <div>
                        <p className="font-semibold">
                          {c.code} <span className="font-normal text-ink/50">({c.discount_percent}% off)</span>
                        </p>
                        <p className="text-xs text-ink/50">
                          {c.redeemed_at ? new Date(c.redeemed_at).toLocaleDateString() : "—"}
                        </p>
                      </div>
                      <span className="rounded-full bg-ink/5 px-2 py-0.5 text-xs font-semibold capitalize">
                        {c.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Referral program */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="merchant-referrals">
              <div className="flex items-center gap-2">
                <Gift size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Referral program</p>
              </div>

              {ref?.referred_by_name && (
                <p className="mt-2 rounded-xl bg-cream px-3 py-2 text-xs text-ink/60">
                  Referred by <span className="font-semibold text-ink">{ref.referred_by_name}</span>{" "}
                  <span className="capitalize">({ref.referred_by_status})</span>
                </p>
              )}

              <p className="mt-3 text-xs uppercase tracking-wide text-ink/50">As a referrer</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <StatCard label="Last 30 days" value={`${ref?.qualified_last_30_days ?? 0} · +${ref?.days_earned_last_30_days ?? 0}d`} tone="bg-mint/60" />
                <StatCard label="Lifetime" value={`${ref?.qualified_total ?? 0} · +${ref?.days_earned_total ?? 0}d`} tone="bg-peach/60" />
              </div>
              {(ref?.pending > 0 || ref?.skipped > 0) && (
                <p className="mt-2 text-center text-xs text-ink/50">
                  {ref.pending} pending, {ref.skipped} skipped (plan lapsed before reward)
                </p>
              )}

              {ref?.recent?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {ref.recent.map((r, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl bg-cream px-3 py-2 text-sm">
                      <p className="font-semibold">
                        {r.referred_name}
                        {r.referred_ig_username && (
                          <span className="font-normal text-ink/50"> @{r.referred_ig_username}</span>
                        )}
                      </p>
                      <div className="text-right">
                        <p className="font-semibold text-hotpink">+{r.reward_days}d</p>
                        <p className="text-xs text-ink/50">{new Date(r.qualified_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Billing history — what THEY'VE paid CheckoutDM, distinct from Business volume above */}
            <div className="rounded-3xl border border-ink/10 bg-white p-5" data-testid="merchant-billing-history">
              <div className="flex items-center gap-2">
                <Receipt size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Billing history</p>
              </div>
              <p className="mt-1 text-xs text-ink/50">Paid to CheckoutDM, not their revenue.</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <StatCard
                  label="Lifetime paid"
                  value={formatMoney(billingHistory?.lifetime_amount, billingHistory?.currency || data.currency)}
                  tone="bg-mint/60"
                />
                <StatCard label="Invoices" value={billingHistory?.invoice_count ?? 0} tone="bg-peach/60" />
              </div>
              {billingHistory?.last_invoice_at && (
                <p className="mt-3 text-center text-xs text-ink/50">
                  Last invoice {new Date(billingHistory.last_invoice_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
